/**
 * Reduce the frames of one bot turn into something a test can assert on.
 *
 * Kept pure and separate from the socket so the interesting decisions — what
 * counts as content, how a split reply is joined, what a "button" is — can be
 * tested without a backend.
 */

/** One thing the bot offered the user to pick. */
export interface BotOption {
  label: string;
  value: string;
}

export interface BotTurn {
  /** Every text frame of this turn, joined in arrival order. */
  text: string;
  /** Options from the LAST frame that carried any — the live prompt. */
  options: BotOption[];
  /** Every frame merged into this turn, verbatim, for the payload view. */
  frames: unknown[];
  /**
   * Milliseconds from the user's message leaving to the FIRST content frame
   * of the reply — what the customer waits before anything appears. Set on a
   * reply to `send`/`sendOption`; absent on a greeting, which answers nothing.
   */
  latencyMs?: number;
  /**
   * Every content frame came from the platform, not the bot — e.g. "This
   * conversation has ended." when the flow ends the chat. Something the
   * customer sees, but not an answer the bot wrote, so a scorer skips it.
   */
  system?: boolean;
  /**
   * Every content frame was written by a person on the business side — a
   * live agent who took the chat over — not by the bot.
   */
  agent?: boolean;
}

/**
 * A frame, reduced to the fields this cares about. Deliberately loose: the
 * generated `IncomingMessage` union is large and lives in pype's codegen,
 * which this package cannot import.
 */
export interface IncomingFrame {
  msg?: string | null;
  msg_type?: string;
  /** Sender: the bot's id, or "system" for the platform's own messages. */
  from?: string;
  /** Which side sent it: "agent" (or "supervisor") for a live agent. */
  from_side?: string;
  /** Set by the backend on a message a person wrote. */
  sender_metadata?: { human_message?: boolean } | null;
  app_object?: {
    type?: string;
    /** The prompt the widget shows above the embed when `msg` is null. */
    label?: string;
    options?: { label?: string; value?: string | number }[];
  } | null;
}

/**
 * `incoming:msg` also carries side-channel frames that are not conversation
 * — `analytics_update` with a null `msg` and no `app_object` is the observed
 * one. Resolving a turn on one of those attributes the NEXT turn's real reply
 * to the wrong question, which shifts every later turn by one and makes a bot
 * that answered correctly look like it deflected.
 */
export const isContentFrame = (frame: IncomingFrame): boolean =>
  frame.msg_type !== 'analytics_update';

/**
 * Written by a person on the business side — pype's `isHumanAuthoredSide`:
 * the backend's own flag first, then the agent/supervisor sides.
 */
export const isAgentFrame = (frame: IncomingFrame): boolean =>
  frame.sender_metadata?.human_message === true ||
  frame.from_side === 'agent' ||
  frame.from_side === 'supervisor';

/**
 * The persistent menu travels as an `app_object` with options too, but the
 * widget shows it as the menu, not as the reply's buttons. Taken as options,
 * it replaced the real prompt on any reply whose last frame had none of its
 * own (a webview, say), offering "Provide Feedback" as the only way on.
 */
const optionsOf = (frame: IncomingFrame): BotOption[] =>
  (frame.app_object?.type === 'persistent_menu'
    ? []
    : (frame.app_object?.options ?? [])
  )
    .filter((o) => o.label !== undefined || o.value !== undefined)
    .map((o) => ({
      label: String(o.label ?? o.value),
      value: String(o.value ?? o.label),
    }));

/**
 * A frame's words, by pype's own rule (`normalize-message.ts`:
 * `msg ?? app_object.label`): a button or listpicker reply often carries its
 * question as the embed's label with a null `msg`, and the widget shows that
 * label as the bubble. Reading `msg` alone left those replies wordless here,
 * so they were scored as saying nothing. The persistent menu is not a reply.
 */
const textOf = (frame: IncomingFrame): string | null | undefined =>
  frame.msg ??
  (frame.app_object?.type === 'persistent_menu'
    ? undefined
    : frame.app_object?.label);

/**
 * Merge the frames of one turn.
 *
 * The bot routinely splits a reply across several frames in quick succession
 * — an answer, then a separate "anything else?" prompt. Treating the first as
 * the whole reply is the misattribution described above.
 */
export const mergeTurn = (frames: IncomingFrame[]): BotTurn => {
  const content = frames.filter(isContentFrame);

  return {
    text: content
      .map(textOf)
      .filter((m): m is string => typeof m === 'string' && m.length > 0)
      .join('\n'),
    // The LAST frame with options wins: an earlier frame's choices have been
    // superseded by the time the turn settles, and offering both would
    // describe a prompt the user never saw.
    options: content.reduce<BotOption[]>((acc, f) => {
      const options = optionsOf(f);
      return options.length ? options : acc;
    }, []),
    frames: content,
    ...(content.length &&
    content.every((f) => f.msg_type === 'system' || f.from === 'system')
      ? { system: true }
      : {}),
    ...(content.length && content.every(isAgentFrame) ? { agent: true } : {}),
  };
};
